/**
 * Notes Contract — The typed API contract for the Research Writing Engine.
 *
 * WHY THIS EXISTS:
 * Defines EXACTLY what the UI expects from the backend for notes.
 * Both the Zustand store and TanStack Query hooks consume only these types.
 * When FastAPI returns NoteResponse, it maps 1:1 to NoteDTO here.
 *
 * BACKEND ALIGNMENT:
 * - FastAPI schema: app/schemas/note.py → NoteResponse (CamelModel)
 * - camelCase aliases: collectionId, linkedDocumentIds, wordCount, createdBy, etc.
 * - OCC: `version` field — client must send back current version on PATCH
 *   to detect concurrent edits (409 Conflict on mismatch)
 *
 * CODEGEN:
 * NoteDTO is derived from the generated zNoteResponse schema (OpenAPI-first).
 * The generated type includes fields not yet surfaced in the UI (visibility,
 * anchorType, anchorPayload, snapshotUrl, useInWriting, noteCitations) — these
 * are optional and safely ignored by dtoToNote() until the UI supports them.
 *
 * RULES:
 * - These types describe wire format only (what goes over the network).
 * - UI components use lib/notes-types.ts Note (richer, TipTap-aware).
 * - dtoToNote() in this file bridges the two representations.
 */

import { z } from 'zod';
import type { JSONContent } from '@tiptap/react';
import type { PaginatedResponse, PaginationParams, SortParams, ApiResponse } from './shared';

import { zNoteResponse as GeneratedNoteResponse } from '@/api/generated/hey-api/zod.gen';

// ─── DTO (derived from generated schema) ───────────────────────────────────

export const NoteDTOSchema = GeneratedNoteResponse;
export type NoteDTO = z.infer<typeof NoteDTOSchema>;

// ─── List params ─────────────────────────────────────────────────────────────
// UI-only: Query param interfaces for frontend list composition.

export type NoteSortField = 'updated_at' | 'created_at' | 'title' | 'word_count';

export interface NoteListFilters {
  search?: string;
  tag?: string;
  starred?: boolean;
  collectionId?: string;
  documentId?: string;
}

/**
 * Query params for GET /notes.
 * workspaceId is required — backend enforces tenant isolation.
 */
export interface NoteListParams
  extends PaginationParams, SortParams<NoteSortField>, NoteListFilters {
  workspaceId: string;
}

// ─── Request types ───────────────────────────────────────────────────────────
// UI-only: Request interfaces for frontend mutation composition.

export interface CreateNoteRequest {
  title: string;
  content?: JSONContent;
  tags?: string[];
  collectionId?: string;
  sessionId?: string;
  linkedDocumentIds?: string[];
  researchObjective?: string;
  documentId?: string;
}

export interface UpdateNoteRequest {
  title?: string;
  content?: JSONContent;
  tags?: string[];
  starred?: boolean;
  collectionId?: string | null;
  linkedDocumentIds?: string[];
  researchObjective?: string | null;
  wordCount?: number;
  /**
   * OCC — pass the current note.version you last received.
   * Backend returns 409 if another update landed since your last read.
   */
  version?: number;
  /**
   * Client-generated UUID for end-to-end save confirmation.
   * Flows: PATCH → Redis buffer → Postgres → note:persisted WS event.
   * Client matches the returned writeId to confirm durability.
   */
  writeId?: string;
  // Visual identity (Phase 1B)
  icon?: string | null;
  coverImageKey?: string | null;
  coverImagePosition?: number | null;
}

export interface MoveNoteRequest {
  parentNoteId: string | null;
  sortOrder: number;
}

// ─── Response types ──────────────────────────────────────────────────────────
// UI-only: Response type aliases for frontend convenience.

export type NoteListResponse = PaginatedResponse<NoteDTO>;
export type NoteDetailResponse = ApiResponse<NoteDTO>;
export type CreateNoteResponse = ApiResponse<NoteDTO>;
export type UpdateNoteResponse = ApiResponse<NoteDTO>;
export type DeleteNoteResponse = ApiResponse<{ id: string; deleted: boolean }>;

// ─── Mapping helper ──────────────────────────────────────────────────────────
// UI-only: Bridge between wire-format NoteDTO and richer Note type.

/**
 * Convert a NoteDTO (wire format) to the richer Note shape used by
 * lib/notes-types.ts and the Zustand store.
 *
 * This is the ONLY place the two representations are bridged.
 */
export function dtoToNote(dto: NoteDTO): import('@/lib/notes-types').Note {
  return {
    id: dto.id,
    title: dto.title,
    content: dto.content as JSONContent | null,
    excerpt: dto.excerpt ?? '',
    tags: dto.tags ?? [],
    starred: dto.starred ?? false,
    collectionId: dto.collectionId ?? undefined,
    sessionId: dto.sessionId ?? undefined,
    linkedDocumentIds: dto.linkedDocumentIds ?? [],
    researchObjective: dto.researchObjective ?? undefined,
    wordCount: dto.wordCount ?? 0,
    version: dto.version ?? 1,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    icon: dto.icon ?? undefined,
    coverImageKey: dto.coverImageKey ?? undefined,
    coverImagePosition: dto.coverImagePosition ?? undefined,
    parentNoteId: dto.parentNoteId ?? undefined,
    sortOrder: dto.sortOrder ?? 0,
    depth: dto.depth ?? 0,
    // The wire format uses an unconstrained string for backwards-compat with
    // older backend versions; narrow it to the canonical NoteVisibility union
    // (`private` | `workspace`). Anything unrecognised collapses to
    // `undefined` — the consumer treats that as workspace-visible-by-default,
    // and the privacy filter in notes/_view.tsx hides notes with unknown
    // owners regardless.
    visibility:
      dto.visibility === 'private' || dto.visibility === 'workspace' ? dto.visibility : undefined,
    createdBy: dto.createdBy ?? null,
  };
}
